import Phaser from 'phaser';
import { NetworkManager } from '../../managers/NetworkManager';
import {
  ClientGameState,
  PlayerPosition,
  PlayerCount,
  RoomJoinedMessage,
  PlayerJoinedMessage,
  GameOverMessage,
  getGameConfig,
  GameLength,
  DEFAULT_GAME_LENGTH,
} from '@hokm/shared';
import { HelpSidebar } from '../../objects/HelpSidebar';
import { LobbyAmbiance } from '../../objects/LobbyAmbiance';
import { MysticalTitle } from '../../objects/MysticalTitle';
import { Toast } from '../../objects/Toast';
import { applyRenderScale } from '../../config';
import { UrlStateManager, JoinConfig } from '../../utils/UrlStateManager';
import { SceneTransition } from '../../utils/SceneTransition';
import { TransitionOverlayScene } from '../TransitionOverlayScene';

// UI Components
import { BackgroundTexture } from './ui/BackgroundTexture';
import { PersianDecorations } from './ui/PersianDecorations';
import { RoomCodeDisplay } from './ui/RoomCodeDisplay';
import { GameButtons } from './ui/GameButtons';
import { PlayerSlots } from './ui/PlayerSlots';

// Controllers
import { InputController } from './controllers/InputController';
import { PlayerCountSelector } from './controllers/PlayerCountSelector';
import { GameLengthSelector } from './controllers/GameLengthSelector';

// Handlers
import { LobbyNetworkHandler } from './handlers/LobbyNetworkHandler';

/** Distinguishes between fresh joins and reconnection attempts */
type JoinMode = 'fresh' | 'reconnecting';

export class LobbyScene extends Phaser.Scene {
  private networkManager!: NetworkManager;
  private selectedPlayerCount: PlayerCount = 4;
  private selectedGameLength: GameLength = DEFAULT_GAME_LENGTH;
  private currentPlayerCount: PlayerCount = 4;
  private myPosition: PlayerPosition = PlayerPosition.SOUTH;
  private myPlayerId: string | null = null;
  private hostId: string | null = null;
  private joinConfig!: JoinConfig;
  private vanityRoomId: string | null = null;
  private joinMode: JoinMode = 'fresh';

  // Ambient animations
  private ambiance!: LobbyAmbiance;
  private mysticalTitle!: MysticalTitle;

  // UI decorations
  private backgroundTexture!: BackgroundTexture;
  private persianDecorations!: PersianDecorations;

  // Modules
  private roomCodeDisplay!: RoomCodeDisplay;
  private gameButtons!: GameButtons;
  private playerSlots!: PlayerSlots;
  private inputController!: InputController;
  private playerCountSelector!: PlayerCountSelector;
  private gameLengthSelector!: GameLengthSelector;
  private networkHandler!: LobbyNetworkHandler;

  constructor() {
    super({ key: 'LobbyScene' });
  }

  create(data?: { fromGame?: boolean }): void {
    applyRenderScale(this);

    if (data?.fromGame) {
      SceneTransition.cardFlyIn(this);
    }

    this.resetState();
    this.initializeManagers();
    this.createVisualElements();
    this.createModules();
    this.setupNetworkHandler();
    this.prefillAndAutoJoin();
  }

  private resetState(): void {
    this.selectedPlayerCount = 4;
    this.selectedGameLength = DEFAULT_GAME_LENGTH;
    this.currentPlayerCount = 4;
    this.myPlayerId = null;
    this.hostId = null;
    this.vanityRoomId = null;
  }

  private initializeManagers(): void {
    this.networkManager = this.registry.get('networkManager');
    this.joinConfig = UrlStateManager.getJoinConfig();
    this.registry.set('joinConfig', this.joinConfig);
  }

  private createVisualElements(): void {
    this.backgroundTexture = new BackgroundTexture(this);
    this.ambiance = new LobbyAmbiance(this);
    this.mysticalTitle = new MysticalTitle(this);
    this.persianDecorations = new PersianDecorations(this);
    new HelpSidebar(this);
  }

  private createModules(): void {
    this.inputController = new InputController(this);

    this.playerCountSelector = new PlayerCountSelector(
      this,
      { onSelectionChanged: (count) => (this.selectedPlayerCount = count) },
      this.selectedPlayerCount
    );

    this.gameLengthSelector = new GameLengthSelector(
      this,
      { onSelectionChanged: (length) => (this.selectedGameLength = length) },
      this.selectedGameLength
    );

    this.gameButtons = new GameButtons(this, {
      onCreateRoom: () => this.handleCreateRoom(),
      onJoinRoom: () => this.handleJoinRoom(),
      onStartGame: () => this.networkManager.startGame(this.selectedGameLength),
      onLeaveRoom: () => this.handleLeaveRoom(),
    });

    this.playerSlots = new PlayerSlots(this, {
      onAddAI: (serverPos) => this.networkManager.addAI(serverPos),
      toServerPosition: (displayPos) => this.toServerPosition(displayPos),
    });

    this.roomCodeDisplay = new RoomCodeDisplay(this, {
      getVanityRoomId: () => this.vanityRoomId,
    });
  }

  private setupNetworkHandler(): void {
    this.networkHandler = new LobbyNetworkHandler(this.networkManager, {
      onRoomJoined: (data) => this.handleRoomJoined(data),
      onPlayerJoined: (displayPos, data) => this.handlePlayerJoined(displayPos, data),
      onPlayerLeft: (displayPos) => this.handlePlayerLeft(displayPos),
      onGameStarted: (state) => this.handleGameStarted(state),
      onGameOver: (state, data) => this.handleGameOver(state, data),
      onLobbyState: (state) => this.updateFromState(state),
      onError: (message) => this.showError(message),
      onKicked: (reason) => this.handleKicked(reason),
      toDisplayPosition: (serverPos) => this.toDisplayPosition(serverPos),
    });
  }

  private syncSelectorsWithConfig(): void {
    if (this.joinConfig.playerCount !== this.selectedPlayerCount) {
      this.selectedPlayerCount = this.joinConfig.playerCount;
      this.playerCountSelector.setSelectedCount(this.joinConfig.playerCount);
    }

    if (this.joinConfig.gameLength !== this.selectedGameLength) {
      this.selectedGameLength = this.joinConfig.gameLength;
      this.gameLengthSelector.setSelectedLength(this.joinConfig.gameLength);
    }
  }

  private prefillAndAutoJoin(): void {
    this.inputController.prefill(this.joinConfig);
    this.syncSelectorsWithConfig();

    if (this.joinConfig.autoJoin && this.joinConfig.roomId) {
      this.handleAutoJoin();
    }
  }

  // === Room Actions ===

  private async handleCreateRoom(): Promise<void> {
    const name = this.inputController.getName();
    try {
      const roomId = await this.networkManager.createRoom(this.selectedPlayerCount);
      await this.networkManager.connect(roomId);
      UrlStateManager.persistName(name);
      this.networkManager.joinRoom(name);
      this.registry.set('playerName', name);
    } catch (error) {
      console.error('Failed to create room:', error);
      this.showError('Failed to create room');
    }
  }

  private async handleJoinRoom(): Promise<void> {
    const name = this.inputController.getName();
    const roomId = this.inputController.getRoomCode();
    if (!roomId) {
      this.showError('Please enter a room code');
      return;
    }
    try {
      await this.networkManager.connect(roomId);
      UrlStateManager.persistName(name);
      this.networkManager.joinRoom(name);
      this.registry.set('playerName', name);
    } catch (error) {
      console.error('Failed to join room:', error);
      this.showError('Failed to join room');
    }
  }

  private async handleAutoJoin(): Promise<void> {
    this.gameButtons.disableJoinButtons();
    this.joinMode = 'reconnecting';

    const { roomId, isVanity, playerName, playerCount } = this.joinConfig;
    if (!roomId) return;

    const savedSession = UrlStateManager.getPersistedSession();
    const playerId = savedSession?.roomId === roomId ? savedSession.playerId : undefined;

    try {
      if (isVanity) {
        this.vanityRoomId = roomId;
        await this.networkManager.connectToVanityRoom(roomId, playerCount);
      } else {
        await this.networkManager.connect(roomId);
      }

      UrlStateManager.persistName(playerName);
      this.networkManager.joinRoom(playerName, playerId);
      this.registry.set('playerName', playerName);
    } catch (error) {
      console.error('Auto-join failed:', error);
      this.networkManager.disconnect();
      this.gameButtons.showJoinUI();
      UrlStateManager.clearRoomParams();
      this.showError('Failed to join room');
    }
  }

  private handleLeaveRoom(): void {
    this.networkManager.disconnect();
    UrlStateManager.clearRoomParams();
    UrlStateManager.clearSession();
    this.vanityRoomId = null;
    this.playerSlots.resetAll();
    this.gameButtons.reset();
    this.inputController.clearRoomCode();

    // Crossfade from room UI back to join UI
    const joinUIContainers = this.getJoinUIContainers();
    const roomUIContainers = this.getRoomUIContainers();

    SceneTransition.crossfade(this, roomUIContainers, joinUIContainers);
  }

  // === Network Event Handlers ===

  private handleRoomJoined(data: RoomJoinedMessage): void {
    // Setup room state before crossfade
    this.setupRoomState(data);

    // Crossfade from join UI to room UI
    const joinUIContainers = this.getJoinUIContainers();
    const roomUIContainers = this.getRoomUIContainers();

    SceneTransition.crossfade(this, joinUIContainers, roomUIContainers);
  }

  private handlePlayerJoined(displayPos: PlayerPosition, data: PlayerJoinedMessage): void {
    this.playerSlots.updateSlot(displayPos, data.player.name, data.player.isAI, data.player.id);
    this.checkStartButton();
  }

  private handlePlayerLeft(displayPos: PlayerPosition): void {
    this.playerSlots.clearSlot(displayPos);
    this.checkStartButton();
  }

  private handleGameStarted(state: ClientGameState): void {
    this.registry.set('gameState', state);

    // Reconnection: skip overlay animation, go directly to GameScene
    if (this.joinMode === 'reconnecting') {
      this.cleanup();
      this.scene.start('GameScene', { reconnecting: true });
      return;
    }

    // Fresh game start: use overlay scatter animation
    // Stop any leftover overlay from previous failed transition
    this.scene.stop('TransitionOverlayScene');

    this.scene.launch('TransitionOverlayScene');
    const overlayScene = this.scene.get('TransitionOverlayScene') as TransitionOverlayScene;

    overlayScene.events.once('create', () => {
      this.scene.bringToTop('TransitionOverlayScene');

      // Start scatter animation, switch to GameScene at midpoint
      overlayScene.startScatter(() => {
        this.cleanup();
        this.scene.start('GameScene', { fromOverlay: true });
      });
    });
  }

  private handleGameOver(state: ClientGameState, data: GameOverMessage): void {
    this.registry.set('gameState', state);
    this.registry.set('finalScore', data);
    this.cleanup();
    SceneTransition.cardFlyOut(this, 'GameOverScene');
  }

  private handleKicked(reason: string): void {
    this.networkManager.disconnect();
    UrlStateManager.clearRoomParams();
    UrlStateManager.clearSession();
    this.vanityRoomId = null;
    this.playerSlots.resetAll();
    this.gameButtons.reset();

    // Crossfade from room UI back to join UI
    const joinUIContainers = this.getJoinUIContainers();
    const roomUIContainers = this.getRoomUIContainers();

    SceneTransition.crossfade(this, roomUIContainers, joinUIContainers);
    this.showError(reason);
  }

  // === UI State Management ===

  /**
   * Setup room state without changing UI visibility (used before crossfade).
   */
  private setupRoomState(data: RoomJoinedMessage): void {
    this.roomCodeDisplay.setRoomCode(data.roomId);

    UrlStateManager.updateUrl({
      room: this.vanityRoomId ? null : data.roomId,
      vanity: this.vanityRoomId ?? null,
    });

    UrlStateManager.persistSession(data.roomId, data.playerId);

    this.currentPlayerCount = data.state.playerCount;
    this.myPosition = data.position;
    this.myPlayerId = data.playerId;
    this.hostId = data.state.hostId;
    this.playerSlots.setPlayerCount(this.currentPlayerCount);
    this.playerSlots.setJoined(true);
    this.playerSlots.showForPlayerCount(this.currentPlayerCount);

    this.updateFromState(data.state);
  }

  /**
   * Get containers for the join UI (for crossfade transitions).
   */
  private getJoinUIContainers(): Phaser.GameObjects.Container[] {
    return [
      this.inputController.getContainer(),
      ...this.gameButtons.getJoinUIContainers(),
      this.playerCountSelector.getContainer(),
      this.gameLengthSelector.getContainer(),
    ];
  }

  /**
   * Get containers for the room UI (for crossfade transitions).
   */
  private getRoomUIContainers(): Phaser.GameObjects.Container[] {
    return [
      this.roomCodeDisplay.getContainer(),
      ...this.gameButtons.getRoomUIContainers(),
      ...this.playerSlots.getContainers(),
    ];
  }

  private updateFromState(state: ClientGameState): void {
    // Keep hostId in sync with server state
    this.hostId = state.hostId;

    state.players.forEach((player) => {
      const displayPos = this.toDisplayPosition(player.position);
      this.playerSlots.updateSlot(displayPos, player.name, player.isAI, player.id);
    });

    // Clear empty slots
    const config = getGameConfig(this.currentPlayerCount);
    for (const serverPos of config.positions) {
      const hasPlayer = state.players.some((p) => p.position === serverPos);
      if (!hasPlayer) {
        const displayPos = this.toDisplayPosition(serverPos as PlayerPosition);
        this.playerSlots.clearSlot(displayPos);
      }
    }

    this.checkStartButton();
  }

  private checkStartButton(): void {
    const filledSlots = this.playerSlots.getFilledSlotCount();
    // Only show START button to the host when all slots are filled
    const shouldShow = this.isHost() && filledSlots === this.currentPlayerCount;
    const wasVisible = this.gameButtons.isStartButtonVisible();

    if (shouldShow && !wasVisible) {
      this.gameButtons.showStartButton();
    } else if (!shouldShow && wasVisible) {
      this.gameButtons.hideStartButton();
    }
  }

  // === Position Helpers ===

  private toDisplayPosition(serverPosition: PlayerPosition): PlayerPosition {
    if (this.currentPlayerCount === 2) {
      return serverPosition === this.myPosition ? PlayerPosition.SOUTH : PlayerPosition.NORTH;
    }
    if (this.currentPlayerCount === 3) {
      return ((serverPosition - this.myPosition + 3) % 3) as PlayerPosition;
    }
    return ((serverPosition - this.myPosition + 4) % 4) as PlayerPosition;
  }

  private toServerPosition(displayPosition: PlayerPosition): PlayerPosition {
    if (this.currentPlayerCount === 2) {
      return displayPosition === PlayerPosition.SOUTH
        ? this.myPosition
        : (((this.myPosition + 2) % 4) as PlayerPosition);
    }
    if (this.currentPlayerCount === 3) {
      return ((displayPosition + this.myPosition) % 3) as PlayerPosition;
    }
    return ((displayPosition + this.myPosition) % 4) as PlayerPosition;
  }

  private isHost(): boolean {
    return this.myPlayerId !== null && this.myPlayerId === this.hostId;
  }

  // === Utilities ===

  private showError(message: string): void {
    new Toast(this, message, 'error', 3000);
    // Re-enable start button in case it was disabled (prevents stuck UI)
    this.gameButtons.enableStartButton();
  }

  update(time: number, delta: number): void {
    this.ambiance.update(time, delta);
    this.mysticalTitle.update(time);
  }

  private cleanup(): void {
    this.networkHandler?.cleanup();
    this.ambiance?.destroy();
    this.mysticalTitle?.destroy();
    this.backgroundTexture?.destroy();
    this.persianDecorations?.destroy();
    this.inputController?.destroy();
    this.roomCodeDisplay?.destroy();
    this.gameButtons?.destroy();
    this.playerSlots?.destroy();
    this.playerCountSelector?.destroy();
    this.gameLengthSelector?.destroy();
  }

  shutdown(): void {
    this.cleanup();
  }
}
